import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import companyService from "../../services/company.service";
import { BANK_NBFC_NAME_LABEL } from "../../constants/companyLabels";
import { REPO_ADMIN_POST_OPTIONS } from "../../constants/repoAdminPost";
import { loadRazorpayScript, openRazorpayCheckout } from "../../utils/razorpayCheckout";
import { getApiErrorMessage } from "../../utils/downloadExcel";
import useAuth from "../../hooks/useAuth";
import { landingPathForRole } from "../../utils/navReturn";
import "../../styles/users.css";

const initialForm = {
  companyName: "",
  email: "",
  phone: "",
  address: "",
  contactPersonName: "",
  adminName: "",
  adminEmail: "",
  adminPhone: "",
  adminPassword: "",
  adminDistrict: "",
  adminPincode: "",
  adminPost: "",
  adminAgencyName: "",
};

const ACTIVATION_SESSION_KEY = "fr_company_activation";

function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function readActivationSession() {
  try {
    const raw = sessionStorage.getItem(ACTIVATION_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.companyId || !parsed?.activationToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeActivationSession(session) {
  sessionStorage.setItem(ACTIVATION_SESSION_KEY, JSON.stringify(session));
}

function clearActivationSession() {
  sessionStorage.removeItem(ACTIVATION_SESSION_KEY);
}

export default function RegisterCompany() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [activation, setActivation] = useState(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    const session = readActivationSession();
    if (session) setActivation(session);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetOtpFlow = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.companyName.trim() || !form.adminName.trim() || !form.adminEmail.trim()) {
      setError(`${BANK_NBFC_NAME_LABEL}, admin name, and admin email are required.`);
      return;
    }
    if (!form.adminPassword.trim() || form.adminPassword.length < 8) {
      setError("Admin password must be at least 8 characters.");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      setError("First confirmation number must be at least 10 digits.");
      return;
    }
    if (form.adminPhone.replace(/\D/g, "").length < 10) {
      setError("Second confirmation number must be at least 10 digits.");
      return;
    }

    setLoading(true);
    try {
      const res = await companyService.registerCompany({
        ...form,
        companyName: form.companyName.trim(),
        adminName: form.adminName.trim(),
        adminEmail: form.adminEmail.trim(),
        adminPhone: form.adminPhone.trim(),
      });
      setRegistrationId(res?.data?.registrationId || "");
      setOtpSent(Boolean(res?.requiresOtp));
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await companyService.verifyCompanyRegistration(registrationId, otp);
      const company = res?.data?.company || {};
      const session = {
        companyId: company.id,
        companyName: company.companyName,
        companyCode: company.companyCode,
        status: company.status || "PENDING",
        activationToken: res?.data?.activationToken,
        activationFee: res?.data?.activationFee,
        contact: form.adminPhone.replace(/\D/g, "").slice(-10),
        email: form.adminEmail.trim(),
        name: form.adminName.trim(),
      };
      writeActivationSession(session);
      setActivation(session);
      setOtpSent(false);
      setOtp("");
      setForm(initialForm);
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || resendCooldown > 0) return;
    setLoading(true);
    setError("");
    try {
      const res = await companyService.resendCompanyRegistrationOtp(registrationId);
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const enterDashboard = async (payload) => {
    const session = payload?.data || payload || {};
    if (!session?.token || !session?.user) return false;
    const permissions = await login({
      user: session.user,
      token: session.token,
    });
    clearActivationSession();
    navigate(landingPathForRole(session.user.role, permissions), { replace: true });
    return true;
  };

  const handlePay = async () => {
    if (!activation?.companyId || !activation?.activationToken) {
      setError("Activation session missing. Please register again.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const orderRes = await companyService.createRegistrationActivationOrder({
        companyId: activation.companyId,
        activationToken: activation.activationToken,
      });
      const data = orderRes?.data || {};
      if (data.alreadyActivated) {
        if (await enterDashboard(orderRes)) return;
        setPaid(true);
        clearActivationSession();
        return;
      }
      await loadRazorpayScript();
      const checkout = await openRazorpayCheckout({
        key: data.razorpayKeyId,
        orderId: data.orderId,
        amount: data.amount,
        currency: data.currency,
        description: "₹100 first-user activation fee",
        prefill: {
          name: form.adminName || activation.name || activation.companyName || "",
          email: form.adminEmail || activation.email || "",
          contact: activation.contact || "",
        },
      });
      const verifyRes = await companyService.verifyRegistrationActivationPayment({
        companyId: activation.companyId,
        activationToken: activation.activationToken,
        razorpay_order_id: checkout.razorpay_order_id,
        razorpay_payment_id: checkout.razorpay_payment_id,
        razorpay_signature: checkout.razorpay_signature,
        paymentId: data.paymentId,
      });
      if (await enterDashboard(verifyRes)) return;
      setPaid(true);
      clearActivationSession();
    } catch (err) {
      if (err?.message === "CHECKOUT_CLOSED") {
        setError("Payment was cancelled. Your company remains pending activation.");
      } else if (err?.message === "PAYMENT_FAILED") {
        setError("Payment failed. Your company remains pending activation.");
      } else {
        setError(
          await getApiErrorMessage(
            err,
            "Payment failed. Your company remains pending activation."
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const feeLabel = formatInr(activation?.activationFee);

  if (paid) {
    return (
      <div className="page create-company-page repo-form-page">
        <div className="create-company-header">
          <div>
            <h2>✓ Payment Successful</h2>
            <p className="muted">Company live — 1 user activation included</p>
          </div>
        </div>
        <div className="create-company-card">
          <p className="cfm-status">
            Registration was free. Your ₹100 payment activated the company and includes
            one user. Additional users are ₹100 each when you activate them.
          </p>
          <div className="create-company-actions">
            <Link to="/home" className="primary-page-btn">
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (activation?.companyId) {
    return (
      <div className="page create-company-page repo-form-page">
        <div className="create-company-header">
          <div>
            <h2>Registration Submitted</h2>
            <p className="muted">
              Registration is free. Pay ₹100 to activate your first user and go live.
            </p>
          </div>
          <Link to="/" className="secondary-page-btn">
            Back to login
          </Link>
        </div>
        <div className="create-company-card">
          <div className="form-grid two-column">
            <div className="form-group">
              <label>Company</label>
              <input value={activation.companyName || ""} readOnly />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input value="PENDING ACTIVATION" readOnly />
            </div>
            <div className="form-group">
              <label>Registration Fee</label>
              <input value="₹0 (Free)" readOnly />
            </div>
            <div className="form-group">
              <label>First User Activation Fee</label>
              <input value={feeLabel} readOnly />
            </div>
            <div className="form-group">
              <label>Refund</label>
              <input value="Non-refundable" readOnly />
            </div>
          </div>
          {error && <p className="error-text">{error}</p>}
          <div className="create-company-actions">
            <button
              type="button"
              className="primary-page-btn"
              onClick={handlePay}
              disabled={loading}
            >
              {loading ? "Opening checkout…" : `Pay ${feeLabel} & Activate`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page create-company-page repo-form-page">
      <div className="create-company-header">
        <div>
          <h2>Register your company</h2>
          <p className="muted">
            Registration is free. After OTP you pay ₹100 to activate your first user.
            Each extra user is ₹100 when you activate them. Monthly plans are separate.
          </p>
        </div>
        <Link to="/" className="secondary-page-btn">
          Back to login
        </Link>
      </div>

      {!otpSent ? (
        <form className="create-company-form" onSubmit={handleSubmit}>
          <div className="create-company-card">
            <h3>Company details</h3>
            <div className="form-grid two-column">
              <div className="form-group">
                <label>{BANK_NBFC_NAME_LABEL} *</label>
                <input name="companyName" value={form.companyName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Company email</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>First confirmation number *</label>
                <input name="phone" value={form.phone} onChange={handleChange} required />
              </div>
              <div className="form-group full-width">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="create-company-card">
            <h3>Repo admin account</h3>
            <div className="form-grid two-column">
              <div className="form-group">
                <label>Admin name *</label>
                <input name="adminName" value={form.adminName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Admin email *</label>
                <input type="email" name="adminEmail" value={form.adminEmail} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Second confirmation number *</label>
                <input name="adminPhone" value={form.adminPhone} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="password" name="adminPassword" value={form.adminPassword} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>District</label>
                <input name="adminDistrict" value={form.adminDistrict} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Pincode</label>
                <input name="adminPincode" value={form.adminPincode} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Post</label>
                <select name="adminPost" value={form.adminPost} onChange={handleChange}>
                  <option value="">Select post</option>
                  {REPO_ADMIN_POST_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Agency name</label>
                <input name="adminAgencyName" value={form.adminAgencyName} onChange={handleChange} />
              </div>
            </div>
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="create-company-actions">
            <button type="button" className="secondary-page-btn" onClick={() => navigate("/repo-admin/login")}>
              Go to repo admin login
            </button>
            <button type="submit" className="primary-page-btn" disabled={loading}>
              {loading ? "Submitting…" : "Submit registration"}
            </button>
          </div>
        </form>
      ) : (
        <form className="create-company-form" onSubmit={handleVerifyOtp}>
          <div className="create-company-card">
            <h3>Verify Mobile Number</h3>
            <p className="muted">
              We sent an OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.
            </p>
            <div className="form-group">
              <label htmlFor="company-registration-otp">Enter OTP</label>
              <input
                id="company-registration-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP"
                required
              />
            </div>
            {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
            {error && <p className="error-text">{error}</p>}
            <div className="create-company-actions">
              <button type="button" className="secondary-page-btn" onClick={resetOtpFlow} disabled={loading}>
                Back / Edit
              </button>
              <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>
                Resend OTP
              </button>
              <button type="submit" className="primary-page-btn" disabled={loading || otp.length !== 6}>
                {loading ? "Verifying…" : "Verify OTP"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
